import type { ButtonAppearance } from '@unifyapps/defs/types/blocks';
import type {
  ConditionalValueFilters,
  ValueOrCondition,
} from '@unifyapps/defs/types/conditionalValueFilters';
import type { UIFilter } from '@unifyapps/defs/types/uiFilter';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { iconSizeVariants } from '@unifyapps/ui/components/Button/styles';
import _compact from 'lodash/compact';
import _find from 'lodash/find';
import { getInlineSvg } from './getInlineSvg';

export type { ConditionalValueFilters, ValueOrCondition };

export type UnComputedConditionalValueFilters<T = unknown> = {
  conditionalValueFilters?: {
    propertyValue: T;
    conditions: { type: 'filter'; payload: UIFilter };
  }[];
  defaultValue?: T;
};

export const getConditionalPropertyValue = <T extends string | number | object | boolean>({
  value,
}: {
  value: ValueOrCondition<T> | undefined;
}): T | undefined => {
  if (typeof value === 'object' && 'conditionalValueFilters' in value) {
    // Only treat explicitly evaluated conditions as truthy to avoid picking the first filter.
    return (
      _find(value.conditionalValueFilters, ({ conditions }) => conditions === true)
        ?.propertyValue ?? value.defaultValue
    );
  }

  return value;
};

export const getConditionalIcon = (value: ValueOrCondition<string> | undefined) => {
  const iconName = getConditionalPropertyValue({ value });
  return getIconFromRegistry(iconName);
};

export const getConditionalIconNode = (
  value: ValueOrCondition<string> | undefined,
  size: ButtonAppearance['size'] = 'md',
) => {
  const iconName = getConditionalPropertyValue({ value });
  const iconClassName = iconSizeVariants({ size });

  return getInlineSvg(iconName, iconClassName);
};

type ColorType = string | { custom: string };

export const getConditionalColor = (value: ValueOrCondition<ColorType> | undefined) => {
  const color = getConditionalPropertyValue({ value });
  if (typeof color === 'object') {
    return color.custom;
  }
  return color;
};

export const getAllPropertyValues = (value: ValueOrCondition<string> | undefined) => {
  if (typeof value === 'object' && 'conditionalValueFilters' in value) {
    return _compact([
      ...value.conditionalValueFilters.map(({ propertyValue }) => propertyValue),
      value.defaultValue,
    ]);
  }

  if (typeof value === 'string') {
    return [value];
  }

  return [];
};
